import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { ThemeProvider } from './context/ThemeContext'
import { ProtectedRoute } from './components/ProtectedRoute'
import { UpdateBanner } from './components/UpdateBanner'
import DeepLinkHandler from './components/DeepLinkHandler'
import { warmupBackend } from './api/client'
import { useHealthKitSync } from './hooks/useHealthKitSync'

import LandingPage from './pages/LandingPage'
import AuthPage from './pages/AuthPage'
import InvitePage from './pages/InvitePage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import CheckInScreen from './screens/CheckIn'
import OnboardingScreen from './screens/Onboarding'
import DomainDetailScreen from './screens/DomainDetail'
import ProtocolScreen from './screens/Protocol'
import EveningWindDownScreen from './screens/EveningWindDown'
import DashboardPage from './screens/Dashboard'
import IntelligenceScreen from './screens/Intelligence'
import SignalHistory from './screens/SignalHistory'
import DayDetail from './screens/DayDetail'
import JournalScreen from './screens/Journal'
import JournalEntryScreen from './screens/JournalEntry'
import CoherenceDashboard from './screens/CoherenceDashboard'
import SignalScreen from './screens/SignalScreen'
import RevealGuard from './components/RevealGuard'
import RevealScreen from './screens/RevealScreen'
import ChatScreen from './screens/Chat'
import ProfileScreen from './screens/Profile'
import ReportScreen from './screens/Report'
import WearablesScreen from './screens/Wearables'
import SettingsPage from './pages/SettingsPage'
import PrivacyPage from './pages/PrivacyPage'
import GetTheApp from './screens/GetTheApp'

function RootRoute() {
  const { user, loading } = useAuth()
  if (loading) return (
    <div style={{
      background: '#F2EDE3',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#9E8456', opacity: 0.6 }} />
    </div>
  )
  if (user) return <Navigate to="/dashboard" replace />
  // app.haiven.co is the application (marketing lives on haiven.co since the
  // domain split). A logged-out visit to the app root — including the
  // home-screen PWA, whose start_url is "/" — should land on the sign-in
  // screen, not the in-app marketing LandingPage. The landing page stays
  // reachable at /welcome for anyone who wants it.
  return <Navigate to="/login" replace />
}

// /coherence is the Day-7 reveal context (CoherenceDashboard). RevealGuard
// still fires the reveal itself via /reveal. Once the reveal has been seen,
// /coherence would otherwise dead-end on the old dashboard — so we send those
// users on to the daily /signal surface instead.
function CoherenceOrSignal() {
  const { user } = useAuth()
  if (user?.has_seen_reveal) return <Navigate to="/signal" replace />
  return <CoherenceDashboard />
}

function DomainDetailRoute() {
  const { code } = useParams()
  return <DomainDetailScreen domainCode={(code || 'bio').toUpperCase()} />
}

function ChatRoute() {
  const navigate = useNavigate()
  const location = useLocation()
  const { seedMessage, seedContext } = location.state || {}
  return <ChatScreen onBack={() => navigate(-1)} seedMessage={seedMessage} seedContext={seedContext} />
}

function ProfileRoute() {
  const navigate = useNavigate()
  const { logout } = useAuth()
  return <ProfileScreen onLogout={() => { logout(); navigate('/login', { replace: true }); }} />
}

function ReportRedirect() {
  // Public report is server-rendered HTML — redirect the browser to the backend
  const { token } = useParams()
  const base = import.meta.env.VITE_API_BASE_URL || ''
  window.location.href = `${base}/report/view/${token}`
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F2EDE3' }}>
      <p style={{ color: '#9E8456', fontSize: 14 }}>Loading report…</p>
    </div>
  )
}

// Needs the auth context, so it mounts inside AuthProvider. Renders
// nothing — just keeps HealthKit data flowing in the iOS native shell.
function HealthKitSync() {
  useHealthKitSync()
  return null
}

export default function App() {
  useEffect(() => { warmupBackend() }, [])

  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          {/* Fixed-position PWA update prompt — renders on every screen
              whenever a new deploy is installed and waiting. */}
          <UpdateBanner />
          <HealthKitSync />
          <DeepLinkHandler />
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/welcome" element={<LandingPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/auth" element={<Navigate to="/login" replace />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Public by design: a fresh invite recipient has no
                account yet and must see the practitioner's identity +
                what they'd request before any login/signup happens. */}
            <Route path="/invite/:token" element={<InvitePage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            {/* Public by design: mid-onboarding users (no
                onboarding_completed_at yet) get bounced off protected
                routes by ProtectedRoute, and this screen must be
                reachable from onboarding screen 5. The install URL it
                shows comes from the unauthenticated /feature-flags. */}
            <Route path="/get-the-app" element={<GetTheApp />} />
            <Route path="/onboarding" element={
              <ProtectedRoute><OnboardingScreen /></ProtectedRoute>
            } />
            <Route path="/checkin" element={
              <ProtectedRoute><CheckInScreen /></ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute><RevealGuard><DashboardPage /></RevealGuard></ProtectedRoute>
            } />
            <Route path="/intelligence" element={
              <ProtectedRoute><IntelligenceScreen /></ProtectedRoute>
            } />
            <Route path="/history" element={
              <ProtectedRoute><SignalHistory /></ProtectedRoute>
            } />
            <Route path="/history/:date" element={
              <ProtectedRoute><DayDetail /></ProtectedRoute>
            } />
            {/* Free-write journal. /journal/new is registered BEFORE
                nothing else claims the segment, but the order matters
                if a /journal/:id detail route is ever added — a bare
                param would otherwise swallow "new". */}
            <Route path="/journal" element={
              <ProtectedRoute><JournalScreen /></ProtectedRoute>
            } />
            <Route path="/journal/new" element={
              <ProtectedRoute><JournalEntryScreen /></ProtectedRoute>
            } />
            {/* Still live, and still the deep view for a single domain —
                no longer bottom-nav-linked, reached from Profile's
                domain summary card instead. */}
            <Route path="/domain/:code" element={
              <ProtectedRoute><DomainDetailRoute /></ProtectedRoute>
            } />
            <Route path="/signal" element={
              <ProtectedRoute><RevealGuard><SignalScreen /></RevealGuard></ProtectedRoute>
            } />
            <Route path="/coherence" element={
              <ProtectedRoute><RevealGuard><CoherenceOrSignal /></RevealGuard></ProtectedRoute>
            } />
            <Route path="/reveal" element={
              <ProtectedRoute><RevealScreen /></ProtectedRoute>
            } />
            <Route path="/protocol" element={
              <ProtectedRoute><ProtocolScreen /></ProtectedRoute>
            } />
            <Route path="/protocol/:id" element={
              <ProtectedRoute><ProtocolScreen /></ProtectedRoute>
            } />
            <Route path="/evening" element={
              <ProtectedRoute><EveningWindDownScreen /></ProtectedRoute>
            } />
            <Route path="/chat" element={
              <ProtectedRoute><ChatRoute /></ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute><ProfileRoute /></ProtectedRoute>
            } />
            <Route path="/wearables" element={
              <ProtectedRoute><WearablesScreen /></ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute><SettingsPage /></ProtectedRoute>
            } />
            {/* In-app report preview — the user sees exactly what a share
                link renders BEFORE any link is minted. Distinct from the
                public /report/view/:token redirect below. */}
            <Route path="/report" element={
              <ProtectedRoute><ReportScreen /></ProtectedRoute>
            } />
            <Route path="/report/view/:token" element={<ReportRedirect />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  )
}
